import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { recipientApiToRecipientEntity, recipientEntityToRecipientApi } from '../../entities/Recipient';
import { pagingMetadataToCursorResponse } from '../../utils/PagingUtils';
import { BaseClient } from '../BaseClient';
export class RecipientClientBase extends BaseClient {
  constructor() {
    super(...arguments);
    _defineProperty(this, "getRecipientImpl", void 0);
    _defineProperty(this, "queryRecipientsImpl", void 0);
    _defineProperty(this, "deleteRecipientImpl", void 0);
    _defineProperty(this, "createRecipientImpl", void 0);
    _defineProperty(this, "getOrCreateRecipientImpl", void 0);
    _defineProperty(this, "associateMetaSiteIdsImpl", void 0);
    _defineProperty(this, "updateRecipientImpl", void 0);
  }
  async getRecipient(recipientId) {
    try {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }
      const {
        recipient
      } = await this.request(this.getRecipientImpl({
        recipientId
      }), ...args);
      return this.convertToEntity(recipient);
    } catch (error) {
      this.logError('getRecipient', 'Failed to get recipient', {
        recipientId
      }, error);
      throw error;
    }
  }
  async getRecipientByMetaSiteId(metaSiteId) {
    try {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      const {
        recipients = []
      } = await this.request(this.queryRecipientsImpl({
        query: {
          filter: {
            metaSiteId
          }
        }
      }), ...args);
      if (recipients.length === 0) {
        throw new Error('No recipient found for metaSiteId');
      } else {
        if (recipients.length > 1) {
          this.logWarn('getRecipientByMetaSiteId', 'Multiple recipients found for metaSiteId', {
            recipients,
            metaSiteId
          });
        }
        return this.convertToEntity(recipients[0]);
      }
    } catch (error) {
      this.logError('getRecipientByMetaSiteId', 'Failed to get recipient', {
        metaSiteId
      }, error);
      throw error;
    }
  }
  async queryRecipients(queryData) {
    try {
      for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
        args[_key3 - 1] = arguments[_key3];
      }
      const {
        recipients,
        pagingMetadata
      } = await this.request(this.queryRecipientsImpl({
        query: {
          cursorPaging: queryData.cursorPaging && {
            ...queryData.cursorPaging,
            cursor: queryData.cursorPaging.cursor ?? undefined
          },
          filter: queryData.filter,
          sort: queryData.sort
        }
      }), ...args);
      return {
        items: (recipients == null ? void 0 : recipients.map(recipient => this.convertToEntity(recipient))) ?? [],
        ...pagingMetadataToCursorResponse(pagingMetadata)
      };
    } catch (error) {
      this.logError('queryRecipients', 'Failed to query recipients', {
        queryData
      }, error);
      throw error;
    }
  }
  async createRecipient(recipient) {
    try {
      for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
        args[_key4 - 1] = arguments[_key4];
      }
      const {
        recipient: createdRecipient
      } = await this.request(this.createRecipientImpl({
        recipient: recipientEntityToRecipientApi(recipient)
      }), ...args);
      return this.convertToEntity(createdRecipient);
    } catch (error) {
      this.logError('createRecipient', 'Failed to create recipient', {
        recipient
      }, error);
      throw error;
    }
  }
  async getOrCreateRecipient(_ref) {
    let {
      email,
      phone = null,
      name = ''
    } = _ref;
    try {
      for (var _len5 = arguments.length, args = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
        args[_key5 - 1] = arguments[_key5];
      }
      const {
        recipient
      } = await this.request(this.getOrCreateRecipientImpl({
        email: email ?? undefined,
        phone: phone ?? undefined,
        name
      }), ...args);
      return this.convertToEntity(recipient);
    } catch (error) {
      this.logError('getOrCreateRecipient', 'Failed to create recipient', {
        email,
        phone,
        name
      }, error);
      throw error;
    }
  }
  async updateRecipientWhatsappConversationId(prevRecipient, activeWhatsappConversationId) {
    try {
      for (var _len6 = arguments.length, args = new Array(_len6 > 2 ? _len6 - 2 : 0), _key6 = 2; _key6 < _len6; _key6++) {
        args[_key6 - 2] = arguments[_key6];
      }
      const {
        recipient: updatedRecipient
      } = await this.request(this.updateRecipientImpl({
        recipient: {
          id: prevRecipient.id,
          revision: prevRecipient.revision,
          ...(activeWhatsappConversationId && {
            activeWhatsappConversationId
          })
        },
        fieldMask: ['activeWhatsappConversationId']
      }), ...args);
      return this.convertToEntity(updatedRecipient);
    } catch (error) {
      this.logError('updateRecipientWhatsappConversationId', 'Failed to update recipient', {
        prevRecipient,
        activeWhatsappConversationId
      }, error);
      throw error;
    }
  }
  async updateRecipientSmsConversationId(prevRecipient, activeSmsConversationId) {
    try {
      for (var _len7 = arguments.length, args = new Array(_len7 > 2 ? _len7 - 2 : 0), _key7 = 2; _key7 < _len7; _key7++) {
        args[_key7 - 2] = arguments[_key7];
      }
      const {
        recipient: updatedRecipient
      } = await this.request(this.updateRecipientImpl({
        recipient: {
          id: prevRecipient.id,
          revision: prevRecipient.revision,
          ...(activeSmsConversationId && {
            activeSmsConversationId
          })
        },
        fieldMask: ['activeSmsConversationId']
      }), ...args);
      return this.convertToEntity(updatedRecipient);
    } catch (error) {
      this.logError('updateRecipientSmsConversationId', 'Failed to update recipient', {
        prevRecipient,
        activeSmsConversationId
      }, error);
      throw error;
    }
  }
  async updateRecipientName(prevRecipient, name) {
    try {
      for (var _len8 = arguments.length, args = new Array(_len8 > 2 ? _len8 - 2 : 0), _key8 = 2; _key8 < _len8; _key8++) {
        args[_key8 - 2] = arguments[_key8];
      }
      const {
        recipient: updatedRecipient
      } = await this.request(this.updateRecipientImpl({
        recipient: {
          id: prevRecipient.id,
          revision: prevRecipient.revision,
          name
        },
        fieldMask: ['name']
      }), ...args);
      return this.convertToEntity(updatedRecipient);
    } catch (error) {
      this.logError('updateRecipientName', 'Failed to update recipient', {
        prevRecipient,
        name
      }, error);
      throw error;
    }
  }
  async updateRecipientFinishedOnboarding(prevRecipient, finishedOnboarding) {
    try {
      for (var _len9 = arguments.length, args = new Array(_len9 > 2 ? _len9 - 2 : 0), _key9 = 2; _key9 < _len9; _key9++) {
        args[_key9 - 2] = arguments[_key9];
      }
      const {
        recipient: updatedRecipient
      } = await this.request(this.updateRecipientImpl({
        recipient: {
          id: prevRecipient.id,
          revision: prevRecipient.revision,
          finishedOnboarding
        },
        fieldMask: ['finishedOnboarding']
      }), ...args);
      return this.convertToEntity(updatedRecipient);
    } catch (error) {
      this.logError('updateRecipientFinishedOnboarding', 'Failed to update recipient', {
        prevRecipient,
        finishedOnboarding
      }, error);
      throw error;
    }
  }
  async deleteRecipient(recipientId) {
    try {
      for (var _len0 = arguments.length, args = new Array(_len0 > 1 ? _len0 - 1 : 0), _key0 = 1; _key0 < _len0; _key0++) {
        args[_key0 - 1] = arguments[_key0];
      }
      await this.request(this.deleteRecipientImpl({
        recipientId
      }), ...args);
    } catch (error) {
      this.logError('deleteRecipient', 'Failed to delete recipient', {
        recipientId
      }, error);
      throw error;
    }
  }
  async associateMetaSiteIds(recipientId, metaSiteIds) {
    try {
      for (var _len1 = arguments.length, args = new Array(_len1 > 2 ? _len1 - 2 : 0), _key1 = 2; _key1 < _len1; _key1++) {
        args[_key1 - 2] = arguments[_key1];
      }
      const {
        recipient
      } = await this.request(this.associateMetaSiteIdsImpl({
        recipientId,
        metaSiteIds
      }), ...args);
      return this.convertToEntity(recipient);
    } catch (error) {
      this.logError('associateMetaSiteIds', 'Failed to associate metasites', {
        recipientId,
        metaSiteIds
      }, error);
      throw error;
    }
  }
  convertToEntity(recipient) {
    try {
      return recipientApiToRecipientEntity(recipient);
    } catch (error) {
      this.logError('convertToEntity', 'Failed to convert recipient', {
        recipient
      }, error);
      throw error;
    }
  }
  getServiceNameForLogs() {
    return 'RecipientClient';
  }
}
//# sourceMappingURL=RecipientClientBase.js.map